import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Calculator, TrendingUp, Users, Euro, Search, FileSpreadsheet, FileText, ArrowUpDown, GitCompare, X, BarChart3, PieChart, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from "recharts";

interface EmployeeWithCosts {
  id: string;
  name: string;
  position: string;
  department: string | null;
  employee_number: string | null;
  is_non_productive: boolean;
  latest_calculation: {
    id: string;
    calculation_date: string;
    brutto_monat: number;
    sollstunden_monat: number;
    selbstkosten_pro_stunde: number | null;
    ziel_stundensatz: number | null;
    ag_pflichtkosten: number | null;
    nebenkosten_summe: number | null;
    gesamtkosten_roh: number | null;
    personalkosten_roh: number | null;
    produktive_kosten_monat: number | null;
    korrekturfaktor: number | null;
    krankenversicherung_prozent: number;
    rentenversicherung_prozent: number;
    pflegeversicherung_prozent: number;
    arbeitslosenversicherung_prozent: number;
    umlage_u1_prozent: number;
    umlage_u2_prozent: number;
    insolvenz_prozent: number;
    arbeitstage_jahr: number;
    urlaubstage: number | null;
    geschaetzte_krankheitstage: number | null;
    feiertage: number | null;
    produktive_tage_jahr: number | null;
    ziel_marge_prozent: number | null;
    risiko_puffer_prozent: number | null;
    overhead_prozent: number | null;
    // Berechnet (nicht aus DB): Umlage unproduktiver Mitarbeiter
    umlage_unproduktiv_monat: number;
    adjusted_gesamtkosten: number | null;
    adjusted_selbstkosten_pro_stunde: number | null;
    adjusted_ziel_stundensatz: number | null;
  } | null;
}

type SortField = "name" | "selbstkosten" | "zielstundensatz" | "brutto";
type SortDirection = "asc" | "desc";

const CostOverviewPage = () => {
  const [employees, setEmployees] = useState<EmployeeWithCosts[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(new Set());
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithCosts | null>(null);
  const { toast } = useToast();

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];

  useEffect(() => {
    fetchEmployeesWithCosts();
  }, []);

  const fetchEmployeesWithCosts = async () => {
    try {
      const { data: employeesData, error: empError } = await supabase
        .from("employees")
        .select("id, name, position, department, employee_number, is_non_productive")
        .is("archived_at", null)
        .order("name");

      if (empError) throw empError;

      const { data: calculationsData, error: calcError } = await supabase
        .from("employee_cost_calculations")
        .select("*")
        .order("calculation_date", { ascending: false });

      if (calcError) throw calcError;

      // Schritt 1: Pro Mitarbeiter die neueste Berechnung bestimmen
      const baseRows = (employeesData || []).map(emp => {
        const empCalculations = (calculationsData || []).filter(c => c.employee_id === emp.id);
        const latestCalc = empCalculations.length > 0 ? empCalculations[0] : null;
        return { emp, latestCalc };
      });

      // Schritt 2: Umlage unproduktiver Mitarbeiter berechnen
      const nonProductive = baseRows.filter(r => r.emp.is_non_productive);
      const productive = baseRows.filter(r => !r.emp.is_non_productive);
      const totalUnproductiveCosts = nonProductive.reduce(
        (sum, r) => sum + (r.latestCalc?.gesamtkosten_roh || 0),
        0
      );
      const umlagePerProductive =
        productive.length > 0 && nonProductive.length > 0
          ? totalUnproductiveCosts / productive.length
          : 0;

      const employeesWithCosts: EmployeeWithCosts[] = baseRows.map(({ emp, latestCalc }) => {
        const isNonProductive = !!emp.is_non_productive;
        const umlage = isNonProductive ? 0 : umlagePerProductive;

        let adjustedGesamt: number | null = null;
        let adjustedSelbst: number | null = null;
        let adjustedZiel: number | null = null;

        if (latestCalc) {
          const gesamt = latestCalc.gesamtkosten_roh || 0;
          adjustedGesamt = gesamt + umlage;

          // Produktive Stunden/Monat aus vorhandenen Werten ableiten,
          // damit der Stundensatz konsistent zur Settings-Berechnung bleibt.
          const baseSelbst = latestCalc.selbstkosten_pro_stunde || 0;
          const produktiveStunden =
            baseSelbst > 0 && gesamt > 0
              ? gesamt / baseSelbst
              : latestCalc.sollstunden_monat || 0;

          if (produktiveStunden > 0) {
            adjustedSelbst = adjustedGesamt / produktiveStunden;
            const zuschlagFaktor =
              1 +
              ((latestCalc.ziel_marge_prozent || 0) +
                (latestCalc.risiko_puffer_prozent || 0) +
                (latestCalc.overhead_prozent || 0)) /
                100;
            adjustedZiel = adjustedSelbst * zuschlagFaktor;
          } else {
            adjustedSelbst = baseSelbst;
            adjustedZiel = latestCalc.ziel_stundensatz;
          }
        }

        return {
          id: emp.id,
          name: emp.name,
          position: emp.position,
          department: emp.department,
          employee_number: emp.employee_number,
          is_non_productive: isNonProductive,
          latest_calculation: latestCalc
            ? {
                id: latestCalc.id,
                calculation_date: latestCalc.calculation_date,
                brutto_monat: latestCalc.brutto_monat,
                sollstunden_monat: latestCalc.sollstunden_monat,
                selbstkosten_pro_stunde: latestCalc.selbstkosten_pro_stunde,
                ziel_stundensatz: latestCalc.ziel_stundensatz,
                ag_pflichtkosten: latestCalc.ag_pflichtkosten,
                nebenkosten_summe: latestCalc.nebenkosten_summe,
                gesamtkosten_roh: latestCalc.gesamtkosten_roh,
                personalkosten_roh: latestCalc.personalkosten_roh,
                produktive_kosten_monat: latestCalc.produktive_kosten_monat,
                korrekturfaktor: latestCalc.korrekturfaktor,
                krankenversicherung_prozent: latestCalc.krankenversicherung_prozent,
                rentenversicherung_prozent: latestCalc.rentenversicherung_prozent,
                pflegeversicherung_prozent: latestCalc.pflegeversicherung_prozent,
                arbeitslosenversicherung_prozent: latestCalc.arbeitslosenversicherung_prozent,
                umlage_u1_prozent: latestCalc.umlage_u1_prozent,
                umlage_u2_prozent: latestCalc.umlage_u2_prozent,
                insolvenz_prozent: latestCalc.insolvenz_prozent,
                arbeitstage_jahr: latestCalc.arbeitstage_jahr,
                urlaubstage: latestCalc.urlaubstage,
                geschaetzte_krankheitstage: latestCalc.geschaetzte_krankheitstage,
                feiertage: latestCalc.feiertage,
                produktive_tage_jahr: latestCalc.produktive_tage_jahr,
                ziel_marge_prozent: latestCalc.ziel_marge_prozent,
                risiko_puffer_prozent: latestCalc.risiko_puffer_prozent,
                overhead_prozent: latestCalc.overhead_prozent,
                umlage_unproduktiv_monat: umlage,
                adjusted_gesamtkosten: adjustedGesamt,
                adjusted_selbstkosten_pro_stunde: adjustedSelbst,
                adjusted_ziel_stundensatz: adjustedZiel,
              }
            : null,
        };
      });

      setEmployees(employeesWithCosts);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Fehler",
        description: "Daten konnten nicht geladen werden",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees
    .filter(emp => {
      const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.employee_number?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesDepartment = departmentFilter === "all" || emp.department === departmentFilter;
      return matchesSearch && matchesDepartment;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "selbstkosten":
          comparison = (a.latest_calculation?.adjusted_selbstkosten_pro_stunde || 0) - 
                      (b.latest_calculation?.adjusted_selbstkosten_pro_stunde || 0);
          break;
        case "zielstundensatz":
          comparison = (a.latest_calculation?.adjusted_ziel_stundensatz || 0) - 
                      (b.latest_calculation?.adjusted_ziel_stundensatz || 0);
          break;
        case "brutto":
          comparison = (a.latest_calculation?.brutto_monat || 0) - 
                      (b.latest_calculation?.brutto_monat || 0);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

  const employeesWithCalc = employees.filter(e => e.latest_calculation);
  const avgSelbstkosten = employeesWithCalc.length > 0
    ? employeesWithCalc.reduce((sum, e) => sum + (e.latest_calculation?.adjusted_selbstkosten_pro_stunde || 0), 0) / employeesWithCalc.length
    : 0;
  const avgZielstundensatz = employeesWithCalc.length > 0
    ? employeesWithCalc.reduce((sum, e) => sum + (e.latest_calculation?.adjusted_ziel_stundensatz || 0), 0) / employeesWithCalc.length
    : 0;
  const totalMonthlyBrutto = employeesWithCalc.reduce((sum, e) => sum + (e.latest_calculation?.brutto_monat || 0), 0);

  // Chart data
  const barChartData = useMemo(() => {
    return employeesWithCalc
      .slice(0, 10)
      .map(emp => ({
        name: emp.name.length > 12 ? emp.name.substring(0, 12) + "..." : emp.name,
        selbstkosten: emp.latest_calculation?.adjusted_selbstkosten_pro_stunde || 0,
        zielstundensatz: emp.latest_calculation?.adjusted_ziel_stundensatz || 0,
      }))
      .sort((a, b) => b.zielstundensatz - a.zielstundensatz);
  }, [employeesWithCalc]);

  const pieChartData = useMemo(() => {
    if (employeesWithCalc.length === 0) return [];
    
    const avgBrutto = employeesWithCalc.reduce((sum, e) => sum + (e.latest_calculation?.brutto_monat || 0), 0) / employeesWithCalc.length;
    const avgAGPflicht = employeesWithCalc.reduce((sum, e) => sum + (e.latest_calculation?.ag_pflichtkosten || 0), 0) / employeesWithCalc.length;
    const avgNebenkosten = employeesWithCalc.reduce((sum, e) => sum + (e.latest_calculation?.nebenkosten_summe || 0), 0) / employeesWithCalc.length;
    
    return [
      { name: "Bruttolohn", value: avgBrutto, color: "hsl(var(--primary))" },
      { name: "AG-Pflichtkosten", value: avgAGPflicht, color: "hsl(var(--chart-2))" },
      { name: "Nebenkosten", value: avgNebenkosten, color: "hsl(var(--chart-3))" },
    ].filter(item => item.value > 0);
  }, [employeesWithCalc]);

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(2)}%`;
  };

  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return value.toFixed(2);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const toggleCompareSelection = (employeeId: string) => {
    const newSelected = new Set(selectedForCompare);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      if (newSelected.size >= 5) {
        toast({
          title: "Maximale Auswahl erreicht",
          description: "Sie können maximal 5 Mitarbeiter gleichzeitig vergleichen",
          variant: "destructive",
        });
        return;
      }
      newSelected.add(employeeId);
    }
    setSelectedForCompare(newSelected);
  };

  const clearCompareSelection = () => {
    setSelectedForCompare(new Set());
  };

  const selectedEmployees = employees.filter(e => selectedForCompare.has(e.id) && e.latest_calculation);

  const exportToExcel = async () => {
    const data = filteredEmployees.map(emp => ({
      "MA-Nr.": emp.employee_number || "-",
      "Name": emp.name,
      "Position": emp.position,
      "Abteilung": emp.department || "-",
      "Unproduktiv": emp.is_non_productive ? "Ja" : "Nein",
      "Brutto/Monat": emp.latest_calculation?.brutto_monat || 0,
      "AG-Pflichtkosten": emp.latest_calculation?.ag_pflichtkosten || 0,
      "Nebenkosten": emp.latest_calculation?.nebenkosten_summe || 0,
      "Gesamtkosten (roh)": emp.latest_calculation?.gesamtkosten_roh || 0,
      "Umlage unproduktive MA": emp.latest_calculation?.umlage_unproduktiv_monat || 0,
      "Gesamtkosten (inkl. Umlage)": emp.latest_calculation?.adjusted_gesamtkosten || 0,
      "Selbstkosten/Std.": emp.latest_calculation?.adjusted_selbstkosten_pro_stunde || 0,
      "Ziel-Stundensatz": emp.latest_calculation?.adjusted_ziel_stundensatz || 0,
      "Berechnung vom": emp.latest_calculation?.calculation_date 
        ? format(new Date(emp.latest_calculation.calculation_date), "dd.MM.yyyy", { locale: de })
        : "-",
    }));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Einstandslöhne");

    // Add headers
    const headers = Object.keys(data[0]);
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };
    });

    // Add data rows
    data.forEach((row) => {
      const values = headers.map((header) => row[header as keyof typeof row]);
      worksheet.addRow(values);
    });

    // Auto-size columns
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? String(cell.value) : "";
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = Math.min(maxLength + 2, 50);
    });

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Einstandsloehne_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);

    toast({ title: "Excel-Export erfolgreich" });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(18);
    doc.text("Einstandslohn-Übersicht", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Stand: ${format(new Date(), "dd.MM.yyyy", { locale: de })}`, pageWidth / 2, 28, { align: "center" });

    doc.setFontSize(12);
    doc.text("Zusammenfassung", 14, 40);
    doc.setFontSize(10);
    doc.text(`Mitarbeiter mit Berechnung: ${employeesWithCalc.length} von ${employees.length}`, 14, 48);
    doc.text(`Ø Selbstkosten/Std.: ${formatCurrency(avgSelbstkosten)}`, 14, 55);
    doc.text(`Ø Ziel-Stundensatz: ${formatCurrency(avgZielstundensatz)}`, 14, 62);

    let y = 75;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Name", 14, y);
    doc.text("Position", 60, y);
    doc.text("Selbstkosten", 120, y);
    doc.text("Ziel-Std.", 155, y);
    doc.text("Datum", 180, y);
    
    doc.setFont("helvetica", "normal");
    y += 8;

    filteredEmployees.forEach((emp) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      
      doc.text(emp.name.substring(0, 25), 14, y);
      doc.text(emp.position.substring(0, 30), 60, y);
      doc.text(emp.latest_calculation?.adjusted_selbstkosten_pro_stunde 
        ? formatCurrency(emp.latest_calculation.adjusted_selbstkosten_pro_stunde) 
        : "-", 120, y);
      doc.text(emp.latest_calculation?.adjusted_ziel_stundensatz 
        ? formatCurrency(emp.latest_calculation.adjusted_ziel_stundensatz) 
        : "-", 155, y);
      doc.text(emp.latest_calculation?.calculation_date 
        ? format(new Date(emp.latest_calculation.calculation_date), "dd.MM.yy", { locale: de })
        : "-", 180, y);
      
      y += 7;
    });

    doc.save(`Einstandsloehne_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    toast({ title: "PDF-Export erfolgreich" });
  };

  const CompareRow = ({ label, values, formatter = formatCurrency, highlight = false }: { 
    label: string; 
    values: (number | null | undefined)[]; 
    formatter?: (v: number | null | undefined) => string;
    highlight?: boolean;
  }) => {
    const numericValues = values.filter((v): v is number => v !== null && v !== undefined);
    const max = numericValues.length > 0 ? Math.max(...numericValues) : null;
    const min = numericValues.length > 0 ? Math.min(...numericValues) : null;

    return (
      <TableRow className={highlight ? "bg-muted/50" : ""}>
        <TableCell className={`font-medium ${highlight ? "font-bold" : ""}`}>{label}</TableCell>
        {values.map((value, idx) => {
          const isMax = value === max && numericValues.length > 1;
          const isMin = value === min && numericValues.length > 1 && min !== max;
          return (
            <TableCell 
              key={idx} 
              className={`text-right ${isMax ? "text-green-600 font-semibold" : ""} ${isMin ? "text-orange-600" : ""} ${highlight ? "font-bold text-primary" : ""}`}
            >
              {formatter(value)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Einstandslohn-Übersicht</h1>
            <p className="text-muted-foreground">Alle Mitarbeiter mit aktuellen Kostenberechnungen</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {selectedForCompare.size > 0 && (
              <>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => setShowCompareDialog(true)}
                  disabled={selectedForCompare.size < 2}
                >
                  <GitCompare className="h-4 w-4 mr-2" />
                  Vergleichen ({selectedForCompare.size})
                </Button>
                <Button variant="ghost" size="sm" onClick={clearCompareSelection}>
                  <X className="h-4 w-4 mr-2" />
                  Auswahl löschen
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Mitarbeiter</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{employeesWithCalc.length} / {employees.length}</div>
              <p className="text-xs text-muted-foreground">mit Berechnung</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Ø Selbstkosten/Std.</CardTitle>
              <Calculator className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(avgSelbstkosten)}</div>
              <p className="text-xs text-muted-foreground">ohne Zuschläge</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Ø Ziel-Stundensatz</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(avgZielstundensatz)}</div>
              <p className="text-xs text-muted-foreground">inkl. Zuschläge</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Σ Bruttolöhne/Monat</CardTitle>
              <Euro className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalMonthlyBrutto)}</div>
              <p className="text-xs text-muted-foreground">Gesamt monatlich</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        {employeesWithCalc.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Bar Chart - Stundensätze */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Stundensätze im Vergleich
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barChartData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}€`} className="text-xs" />
                      <YAxis dataKey="name" type="category" width={75} className="text-xs" tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(value: number) => [`${value.toFixed(2)}€`, ""]}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        labelStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      <Bar dataKey="selbstkosten" name="Selbstkosten/Std." fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="zielstundensatz" name="Ziel-Stundensatz" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-primary" />
                    <span className="text-muted-foreground">Selbstkosten/Std.</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: 'hsl(var(--chart-2))' }} />
                    <span className="text-muted-foreground">Ziel-Stundensatz</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pie Chart - Kostenverteilung */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Durchschnittliche Kostenverteilung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => [`${value.toFixed(2)}€`, ""]}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap justify-center gap-3 mt-2 text-xs">
                  {pieChartData.map((entry, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
                      <span className="text-muted-foreground">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach Name, Position oder MA-Nr..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Abteilung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Abteilungen</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept!}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Umlage-Info */}
        {(() => {
          const nonProd = employees.filter(e => e.is_non_productive);
          const prod = employees.filter(e => !e.is_non_productive);
          const umlage = prod.length > 0 && nonProd.length > 0
            ? prod[0]?.latest_calculation?.umlage_unproduktiv_monat || 0
            : 0;
          const totalUnprod = nonProd.reduce((s, e) => s + (e.latest_calculation?.gesamtkosten_roh || 0), 0);
          if (nonProd.length === 0 || umlage === 0) return null;
          return (
            <Card className="mb-4 border-amber-500/40 bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="pt-4 text-sm">
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-900 dark:text-amber-200">
                      Kostenumlage unproduktiver Mitarbeiter aktiv
                    </p>
                    <p className="text-amber-900/80 dark:text-amber-200/80 mt-1">
                      {nonProd.length} unproduktive(r) Mitarbeiter mit Gesamtkosten von{" "}
                      <span className="font-medium">{formatCurrency(totalUnprod)}/Monat</span> werden auf{" "}
                      {prod.length} produktive Mitarbeiter umgelegt: <span className="font-medium">{formatCurrency(umlage)}/Monat</span> je produktivem Mitarbeiter.
                      Selbstkosten und Ziel-Stundensätze unten sind bereits inkl. Umlage.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Hint for comparison */}
        {selectedForCompare.size === 0 && (
          <p className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
            <GitCompare className="h-4 w-4" />
            Wählen Sie Mitarbeiter aus, um deren Einstandslöhne zu vergleichen
          </p>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <span className="sr-only">Auswählen</span>
                  </TableHead>
                  <TableHead className="w-[60px]">MA-Nr.</TableHead>
                  <TableHead>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2 -ml-2"
                      onClick={() => handleSort("name")}
                    >
                      Name
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">Position</TableHead>
                  <TableHead className="hidden lg:table-cell">Abteilung</TableHead>
                  <TableHead className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2"
                      onClick={() => handleSort("brutto")}
                    >
                      Brutto/Monat
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2"
                      onClick={() => handleSort("selbstkosten")}
                    >
                      Selbstkosten/Std.
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2"
                      onClick={() => handleSort("zielstundensatz")}
                    >
                      Ziel-Stundensatz
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell text-right">Datum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Keine Mitarbeiter gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map(emp => (
                    <TableRow 
                      key={emp.id} 
                      className={`${selectedForCompare.has(emp.id) ? "bg-primary/5" : ""} ${emp.latest_calculation ? "cursor-pointer hover:bg-muted/50" : ""}`}
                      onClick={() => emp.latest_calculation && setSelectedEmployee(emp)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedForCompare.has(emp.id)}
                          onCheckedChange={() => toggleCompareSelection(emp.id)}
                          disabled={!emp.latest_calculation}
                          aria-label={`${emp.name} zum Vergleich auswählen`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {emp.employee_number || "-"}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          {emp.name}
                          {emp.is_non_productive && (
                            <Badge variant="secondary" className="text-[10px] uppercase">
                              Unproduktiv
                            </Badge>
                          )}
                          {emp.latest_calculation && (
                            <Eye className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {emp.position}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {emp.department ? (
                          <Badge variant="outline">{emp.department}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {emp.latest_calculation ? (
                          formatCurrency(emp.latest_calculation.brutto_monat)
                        ) : (
                          <Badge variant="secondary" className="text-xs">Keine Daten</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {emp.latest_calculation?.adjusted_selbstkosten_pro_stunde ? (
                          <div className="flex flex-col items-end">
                            <span>{formatCurrency(emp.latest_calculation.adjusted_selbstkosten_pro_stunde)}</span>
                            {!emp.is_non_productive && (emp.latest_calculation.umlage_unproduktiv_monat || 0) > 0 && (
                              <span className="text-[10px] text-muted-foreground">inkl. Umlage</span>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right font-bold text-primary">
                        {emp.latest_calculation?.adjusted_ziel_stundensatz ? (
                          formatCurrency(emp.latest_calculation.adjusted_ziel_stundensatz)
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-right text-muted-foreground text-sm">
                        {emp.latest_calculation?.calculation_date ? (
                          format(new Date(emp.latest_calculation.calculation_date), "dd.MM.yyyy", { locale: de })
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

      {/* Compare Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Mitarbeiter-Vergleich
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px] sticky left-0 bg-background">Kennzahl</TableHead>
                  {selectedEmployees.map(emp => (
                    <TableHead key={emp.id} className="text-center min-w-[140px]">
                      <div className="font-bold">{emp.name}</div>
                      <div className="text-xs text-muted-foreground font-normal">{emp.position}</div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Block A: Lohnbasis */}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={selectedEmployees.length + 1} className="font-bold text-sm">
                    Block A: Lohnbasis
                  </TableCell>
                </TableRow>
                <CompareRow 
                  label="Brutto/Monat" 
                  values={selectedEmployees.map(e => e.latest_calculation?.brutto_monat)} 
                />
                <CompareRow 
                  label="Sollstunden/Monat" 
                  values={selectedEmployees.map(e => e.latest_calculation?.sollstunden_monat)} 
                  formatter={formatNumber}
                />

                {/* Block B: AG-Pflichtkosten */}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={selectedEmployees.length + 1} className="font-bold text-sm">
                    Block B: AG-Pflichtkosten
                  </TableCell>
                </TableRow>
                <CompareRow 
                  label="Krankenversicherung" 
                  values={selectedEmployees.map(e => e.latest_calculation?.krankenversicherung_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="Rentenversicherung" 
                  values={selectedEmployees.map(e => e.latest_calculation?.rentenversicherung_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="Pflegeversicherung" 
                  values={selectedEmployees.map(e => e.latest_calculation?.pflegeversicherung_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="Arbeitslosenversicherung" 
                  values={selectedEmployees.map(e => e.latest_calculation?.arbeitslosenversicherung_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="AG-Pflichtkosten (€)" 
                  values={selectedEmployees.map(e => e.latest_calculation?.ag_pflichtkosten)} 
                />
                <CompareRow 
                  label="Personalkosten roh" 
                  values={selectedEmployees.map(e => e.latest_calculation?.personalkosten_roh)} 
                />

                {/* Block C: Nebenkosten */}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={selectedEmployees.length + 1} className="font-bold text-sm">
                    Block C: Nebenkosten
                  </TableCell>
                </TableRow>
                <CompareRow 
                  label="Nebenkosten Summe" 
                  values={selectedEmployees.map(e => e.latest_calculation?.nebenkosten_summe)} 
                />
                <CompareRow 
                  label="Gesamtkosten roh" 
                  values={selectedEmployees.map(e => e.latest_calculation?.gesamtkosten_roh)} 
                />

                {/* Block D: Produktivität */}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={selectedEmployees.length + 1} className="font-bold text-sm">
                    Block D: Produktivität
                  </TableCell>
                </TableRow>
                <CompareRow 
                  label="Arbeitstage/Jahr" 
                  values={selectedEmployees.map(e => e.latest_calculation?.arbeitstage_jahr)} 
                  formatter={v => v?.toString() || "-"}
                />
                <CompareRow 
                  label="Urlaubstage" 
                  values={selectedEmployees.map(e => e.latest_calculation?.urlaubstage)} 
                  formatter={v => v?.toString() || "-"}
                />
                <CompareRow 
                  label="Krankheitstage (gesch.)" 
                  values={selectedEmployees.map(e => e.latest_calculation?.geschaetzte_krankheitstage)} 
                  formatter={v => v?.toString() || "-"}
                />
                <CompareRow 
                  label="Feiertage" 
                  values={selectedEmployees.map(e => e.latest_calculation?.feiertage)} 
                  formatter={v => v?.toString() || "-"}
                />
                <CompareRow 
                  label="Produktive Tage/Jahr" 
                  values={selectedEmployees.map(e => e.latest_calculation?.produktive_tage_jahr)} 
                  formatter={v => v?.toString() || "-"}
                />
                <CompareRow 
                  label="Korrekturfaktor" 
                  values={selectedEmployees.map(e => e.latest_calculation?.korrekturfaktor)} 
                  formatter={formatNumber}
                />
                <CompareRow 
                  label="Produktive Kosten/Monat" 
                  values={selectedEmployees.map(e => e.latest_calculation?.produktive_kosten_monat)} 
                />

                {/* Block E: Ergebnis */}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={selectedEmployees.length + 1} className="font-bold text-sm">
                    Block E: Stundenkosten & Zuschläge
                  </TableCell>
                </TableRow>
                <CompareRow 
                  label="Zielmarge" 
                  values={selectedEmployees.map(e => e.latest_calculation?.ziel_marge_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="Risikopuffer" 
                  values={selectedEmployees.map(e => e.latest_calculation?.risiko_puffer_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="Overhead" 
                  values={selectedEmployees.map(e => e.latest_calculation?.overhead_prozent)} 
                  formatter={formatPercent}
                />
                <CompareRow 
                  label="Selbstkosten/Std. (roh)" 
                  values={selectedEmployees.map(e => e.latest_calculation?.selbstkosten_pro_stunde)} 
                />
                <CompareRow 
                  label="Umlage unprod. MA/Monat" 
                  values={selectedEmployees.map(e => e.latest_calculation?.umlage_unproduktiv_monat)} 
                />
                <CompareRow 
                  label="Selbstkosten/Std. (inkl. Umlage)" 
                  values={selectedEmployees.map(e => e.latest_calculation?.adjusted_selbstkosten_pro_stunde)} 
                  highlight
                />
                <CompareRow 
                  label="Ziel-Stundensatz (inkl. Umlage)" 
                  values={selectedEmployees.map(e => e.latest_calculation?.adjusted_ziel_stundensatz)} 
                  highlight
                />

                {/* Berechnungsdatum */}
                <TableRow className="border-t-2">
                  <TableCell className="font-medium text-muted-foreground">Berechnung vom</TableCell>
                  {selectedEmployees.map(emp => (
                    <TableCell key={emp.id} className="text-right text-muted-foreground text-sm">
                      {emp.latest_calculation?.calculation_date 
                        ? format(new Date(emp.latest_calculation.calculation_date), "dd.MM.yyyy", { locale: de })
                        : "-"
                      }
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Sheet */}
      <Sheet open={!!selectedEmployee} onOpenChange={() => setSelectedEmployee(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Kostendetails
            </SheetTitle>
            {selectedEmployee && (
              <div className="text-left">
                <p className="text-lg font-semibold">{selectedEmployee.name}</p>
                <p className="text-sm text-muted-foreground">{selectedEmployee.position}</p>
                {selectedEmployee.department && (
                  <Badge variant="outline" className="mt-1">{selectedEmployee.department}</Badge>
                )}
              </div>
            )}
          </SheetHeader>

          {selectedEmployee?.latest_calculation && (
            <div className="space-y-6">
              {/* Block A: Lohnbasis */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-primary">
                  <Euro className="h-4 w-4" />
                  Block A: Lohnbasis
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Brutto/Monat</div>
                  <div className="text-right font-medium">{formatCurrency(selectedEmployee.latest_calculation.brutto_monat)}</div>
                  <div className="text-muted-foreground">Sollstunden/Monat</div>
                  <div className="text-right font-medium">{selectedEmployee.latest_calculation.sollstunden_monat} Std.</div>
                </div>
              </div>

              {/* Block B: AG-Pflichtkosten */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-primary">
                  <TrendingUp className="h-4 w-4" />
                  Block B: Arbeitgeber-Pflichtkosten
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Krankenversicherung</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.krankenversicherung_prozent)}</div>
                  <div className="text-muted-foreground">Rentenversicherung</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.rentenversicherung_prozent)}</div>
                  <div className="text-muted-foreground">Pflegeversicherung</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.pflegeversicherung_prozent)}</div>
                  <div className="text-muted-foreground">Arbeitslosenversicherung</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.arbeitslosenversicherung_prozent)}</div>
                  <div className="text-muted-foreground">Umlage U1</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.umlage_u1_prozent)}</div>
                  <div className="text-muted-foreground">Umlage U2</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.umlage_u2_prozent)}</div>
                  <div className="text-muted-foreground">Insolvenzumlage</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.insolvenz_prozent)}</div>
                  <div className="text-muted-foreground font-medium border-t pt-2">AG-Pflichtkosten</div>
                  <div className="text-right font-medium border-t pt-2">{formatCurrency(selectedEmployee.latest_calculation.ag_pflichtkosten)}</div>
                  <div className="text-muted-foreground font-medium">Personalkosten roh</div>
                  <div className="text-right font-medium">{formatCurrency(selectedEmployee.latest_calculation.personalkosten_roh)}</div>
                </div>
              </div>

              {/* Block C: Nebenkosten */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-primary">
                  <Calculator className="h-4 w-4" />
                  Block C: Nebenkosten
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground font-medium">Nebenkosten gesamt</div>
                  <div className="text-right font-medium">{formatCurrency(selectedEmployee.latest_calculation.nebenkosten_summe)}</div>
                  <div className="text-muted-foreground font-medium">Gesamtkosten roh</div>
                  <div className="text-right font-medium">{formatCurrency(selectedEmployee.latest_calculation.gesamtkosten_roh)}</div>
                </div>
              </div>

              {/* Block D: Produktivität */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-primary">
                  <Users className="h-4 w-4" />
                  Block D: Produktivitätskorrektur
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Arbeitstage/Jahr</div>
                  <div className="text-right">{selectedEmployee.latest_calculation.arbeitstage_jahr}</div>
                  <div className="text-muted-foreground">Urlaubstage</div>
                  <div className="text-right">{selectedEmployee.latest_calculation.urlaubstage || 0}</div>
                  <div className="text-muted-foreground">Krankheitstage (geschätzt)</div>
                  <div className="text-right">{selectedEmployee.latest_calculation.geschaetzte_krankheitstage || 0}</div>
                  <div className="text-muted-foreground">Feiertage</div>
                  <div className="text-right">{selectedEmployee.latest_calculation.feiertage || 0}</div>
                  <div className="text-muted-foreground font-medium border-t pt-2">Produktive Tage/Jahr</div>
                  <div className="text-right font-medium border-t pt-2">{selectedEmployee.latest_calculation.produktive_tage_jahr || "-"}</div>
                  <div className="text-muted-foreground font-medium">Korrekturfaktor</div>
                  <div className="text-right font-medium">{formatNumber(selectedEmployee.latest_calculation.korrekturfaktor)}</div>
                  <div className="text-muted-foreground font-medium">Produktive Kosten/Monat</div>
                  <div className="text-right font-medium">{formatCurrency(selectedEmployee.latest_calculation.produktive_kosten_monat)}</div>
                </div>
              </div>

              {/* Block E: Stundenkosten & Ziel */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 text-primary">
                  <TrendingUp className="h-4 w-4" />
                  Block E: Stundenkosten & Ziel
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Ziel-Marge</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.ziel_marge_prozent)}</div>
                  <div className="text-muted-foreground">Risiko-Puffer</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.risiko_puffer_prozent)}</div>
                  <div className="text-muted-foreground">Overhead</div>
                  <div className="text-right">{formatPercent(selectedEmployee.latest_calculation.overhead_prozent)}</div>
                </div>
                {(selectedEmployee.latest_calculation.umlage_unproduktiv_monat || 0) > 0 && (
                  <div className="grid grid-cols-2 gap-2 text-sm mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-500/30 rounded-lg">
                    <div className="text-amber-900 dark:text-amber-200 font-medium">Umlage unproduktiver Mitarbeiter</div>
                    <div className="text-right font-medium text-amber-900 dark:text-amber-200">
                      +{formatCurrency(selectedEmployee.latest_calculation.umlage_unproduktiv_monat)}/Monat
                    </div>
                    <div className="text-muted-foreground text-xs col-span-2">
                      Selbstkosten roh: {formatCurrency(selectedEmployee.latest_calculation.selbstkosten_pro_stunde)} → inkl. Umlage angepasst.
                    </div>
                  </div>
                )}
                {selectedEmployee.is_non_productive && (
                  <div className="mt-4 p-3 bg-muted rounded-lg text-xs text-muted-foreground">
                    Dieser Mitarbeiter ist als <span className="font-semibold">unproduktiv</span> markiert.
                    Seine Gesamtkosten werden auf die produktiven Mitarbeiter umgelegt.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-sm mt-4 p-3 bg-muted/50 rounded-lg">
                  <div className="font-semibold">Selbstkosten/Std.{(selectedEmployee.latest_calculation.umlage_unproduktiv_monat || 0) > 0 && " (inkl. Umlage)"}</div>
                  <div className="text-right font-bold text-lg">{formatCurrency(selectedEmployee.latest_calculation.adjusted_selbstkosten_pro_stunde)}</div>
                  <div className="font-semibold text-primary">Ziel-Stundensatz{(selectedEmployee.latest_calculation.umlage_unproduktiv_monat || 0) > 0 && " (inkl. Umlage)"}</div>
                  <div className="text-right font-bold text-lg text-primary">{formatCurrency(selectedEmployee.latest_calculation.adjusted_ziel_stundensatz)}</div>
                </div>
              </div>

              {/* Berechnungsdatum */}
              <div className="text-xs text-muted-foreground text-center pt-4 border-t">
                Berechnung vom {format(new Date(selectedEmployee.latest_calculation.calculation_date), "dd.MM.yyyy", { locale: de })}
              </div>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setSelectedEmployee(null)}>
              Schließen
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CostOverviewPage;
